import { useEffect, useState } from 'react'
import { Inbox } from 'lucide-react'
import { listMaintenanceEvents } from '~/api/maintenance'
import { getTimeseries } from '~/api/stats'
import { useStats } from '~/api/use-stats'
import type { AdminMaintenanceEvent, TimeseriesPoint } from '~/api/types'
import { ChartCard, SeriesArea, KRDS } from '~/components/charts'
import { FeedbackPanel } from '~/components/FeedbackPanel'
import { MaintenanceDetailPanel } from '~/components/MaintenanceDetailPanel'
import { MaintenanceTable } from '~/components/MaintenanceTable'
import { ServicePageShell } from '~/components/ServicePageShell'
import { StatCard } from '~/components/StatCard'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { useToast } from '~/components/ui/toast'
import { formatDateTime, whoLabel } from '~/lib/utils'

const TREND_DAYS = 30

// 변경 내용 요약(미니 테이블용). 길면 잘라서 한 줄로.
function summarize(text: string | null): string {
    if (!text) return '-'
    const trimmed = text.trim()
    return trimmed.length > 60 ? `${trimmed.slice(0, 60)}…` : trimmed
}

// 유지하기 서비스 페이지. 개요(KPI + 변경 추이 + 최근 이벤트) + 데이터(목록 → 상세) 탭.
export function MaintenancePage() {
    const toast = useToast()
    const { stats, error } = useStats()
    const [selected, setSelected] = useState<AdminMaintenanceEvent | null>(null)
    const [series, setSeries] = useState<TimeseriesPoint[] | null>(null)
    const [recent, setRecent] = useState<AdminMaintenanceEvent[] | null>(null)
    const m = stats?.maintenance

    // 통계 조회 오류는 토스트로 알린다.
    useEffect(() => {
        if (error) toast.error(error)
    }, [error, toast])

    // 개요 시각화 데이터: 30일 추이 + 최근 5건.
    useEffect(() => {
        let alive = true
        getTimeseries(TREND_DAYS)
            .then((ts) => alive && setSeries(ts.series))
            .catch(() => alive && setSeries([]))
        listMaintenanceEvents({ limit: 5 })
            .then((rows) => alive && setRecent(rows))
            .catch(() => alive && setRecent([]))
        return () => {
            alive = false
        }
    }, [])

    const trendEmpty =
        series !== null && series.every((p) => p.maintenance === 0)

    const overview = (
        <>
            <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
                <StatCard
                    label='변경 이벤트'
                    value={m?.events ?? '-'}
                    tone='primary'
                />
                <StatCard
                    label='최근 7일'
                    value={m?.events7d ?? '-'}
                    hint='최근 7일 변경'
                    tone='info'
                />
            </div>

            <div className='mt-6'>
                <ChartCard
                    title='일별 변경 이벤트 추이'
                    description={`HACCP 계획 변경 이벤트 · 최근 ${TREND_DAYS}일`}
                    empty={series === null || trendEmpty}
                    emptyText='기간 내 변경 이벤트가 없어요.'>
                    <SeriesArea
                        data={series ?? []}
                        dataKey='maintenance'
                        name='변경 이벤트'
                        color={KRDS.warning}
                    />
                </ChartCard>
            </div>

            <Card className='mt-4 p-5'>
                <h3 className='krds-title text-foreground mb-3'>
                    최근 변경 이벤트
                </h3>
                {recent !== null && recent.length === 0 ? (
                    <EmptyState
                        icon={Inbox}
                        title='변경 이벤트가 없어요.'
                        description='HACCP 계획 변경 이벤트가 기록되면 여기에 표시돼요.'
                    />
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>변경 내용</TableHead>
                                <TableHead>사용자</TableHead>
                                <TableHead>평가 ID</TableHead>
                                <TableHead>생성일</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {recent === null
                                ? Array.from({ length: 5 }).map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell>
                                              <Skeleton className='h-4 w-48' />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton className='h-4 w-28' />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton className='h-4 w-16' />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton className='h-4 w-28' />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : recent.map((e) => (
                                      <TableRow
                                          key={e.id}
                                          onClick={() => setSelected(e)}
                                          className='cursor-pointer'>
                                          <TableCell className='text-foreground'>
                                              {summarize(e.changeDescription)}
                                          </TableCell>
                                          <TableCell
                                              className='max-w-[180px] truncate'
                                              title={e.ownerEmail ?? undefined}>
                                              {whoLabel(
                                                  e.ownerName,
                                                  e.ownerEmail
                                              )}
                                          </TableCell>
                                          <TableCell
                                              className='text-muted-foreground max-w-[140px] truncate font-mono text-xs'
                                              title={
                                                  e.assessmentId ?? undefined
                                              }>
                                              {e.assessmentId ?? '-'}
                                          </TableCell>
                                          <TableCell className='whitespace-nowrap'>
                                              {formatDateTime(e.createdAt)}
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </>
    )

    const data = (
        <MaintenanceTable selectedId={selected?.id} onSelect={setSelected} />
    )

    return (
        <>
            <ServicePageShell
                title='유지하기'
                description='HACCP 유지관리 서비스 개요'
                tabs={[
                    { label: '개요', content: overview },
                    { label: '데이터', content: data },
                    {
                        label: '피드백',
                        content: <FeedbackPanel service='maintenance' />,
                    },
                ]}
            />
            {/* 상세 패널은 페이지 레벨 — 개요·데이터 어느 탭에서 선택하든 열린다. */}
            {selected ? (
                <MaintenanceDetailPanel
                    eventId={selected.id}
                    onClose={() => setSelected(null)}
                />
            ) : null}
        </>
    )
}
